import { sleep } from 'src/utils/sleep'
import { isIOSWebview } from 'src/utils/userAgent'
import { callJsBridgeNativeHandler, createJsBridgeCallback } from '.'

/**
 * Copy content to the mobile native clipboard.
 */
export function copyToJsBridge(content: string) {
  return callJsBridgeNativeHandler('copyToClipboard', { content })
}

/**
 * Get the mobile app version.
 *
 * @deprecated Use `getAppMetadata` instead.
 */
export async function getAppVersionFromBridge(): Promise<string | undefined> {
  return await Promise.race([
    new Promise<string>(resolve => {
      const callbackId = createJsBridgeCallback((data: { version: string }) => {
        resolve(data.version)
      })
      callJsBridgeNativeHandler('getAppVersion', undefined, callbackId)
    }),
    sleep(1000) as Promise<undefined>,
  ])
}

type AppMetadata = {
  platform?: 'Android' | 'iOS'
  version: string
  osVersion?: string
  deviceModel?: string
}

/**
 * Get metadata about the mobile app
 */
export async function getAppMetadata(): Promise<AppMetadata | undefined> {
  let success = false
  return await Promise.race([
    new Promise<AppMetadata>(resolve => {
      const callbackId = createJsBridgeCallback((data: AppMetadata) => {
        success = true
        resolve(data)
      })
      callJsBridgeNativeHandler('getAppMetadata', undefined, callbackId)
    }),
    sleep(1000)
      .then(() => {
        if (success) return
        // Fallback
        return getAppVersionFromBridge()
      })
      .then(version => (version ? { version } : undefined)),
  ])
}

/**
 * A naive version comparison function
 *
 * This function does NOT follow the common rules of semantic versioning.
 * This is to make testing easier when using TF versions like "3.19.0-beta1".
 * E.g., 3.19.0-beta1 is considered equal to 3.19.
 */
export async function isAppVersionGreaterThanOrEqualTo(target?: string) {
  if (!target) return false

  const { version: appVer } = (await getAppMetadata()) ?? {}
  if (!appVer) return false

  // Parse version by splitting on dots and hyphens, but keep only numeric parts
  const parseVersion = (version: string): number[] => {
    const parts = version.split(/[.-]/)
    const numericParts: number[] = []

    for (const part of parts) {
      // Only include parts that are purely numeric
      if (/^\d+$/.test(part)) {
        numericParts.push(Number(part))
      }
      // Skip non-numeric parts like "beta1", "alpha", etc.
    }

    return numericParts
  }

  const appVerParts = parseVersion(appVer)
  const targetParts = parseVersion(target)

  // Compare versions part by part
  const maxLength = Math.max(appVerParts.length, targetParts.length)
  for (let i = 0; i < maxLength; i++) {
    const appPart = appVerParts[i] || 0
    const targetPart = targetParts[i] || 0

    if (appPart < targetPart) return false
    if (appPart > targetPart) return true
  }

  return true
}

/**
 * Navigate to a native page in the mobile app.
 */
export function navigateToNativePage(args: { route: string }) {
  callJsBridgeNativeHandler('navigate', args)
}

/**
 * Navigate back in the mobile app.
 */
export function popBack() {
  callJsBridgeNativeHandler('popBack')
}

const IOS_MOBILE_PRODUCT_IDS: Record<string, string> = {
  'extra-package': 'consumable.extra.package',
  'extra-package-2': 'consumable.extra.package.2',
  'extra-package-3': 'consumable.extra.package.3',
}
const ANDROID_MOBILE_PRODUCT_IDS: Record<string, string> = {
  'extra-package': 'iap_credits_pack',
  'extra-package-2': 'iap_credits_pack_2',
  'extra-package-3': 'iap_credits_pack_3',
}
const MOBILE_PRODUCT_IDS =
  typeof window !== 'undefined' && isIOSWebview()
    ? IOS_MOBILE_PRODUCT_IDS
    : ANDROID_MOBILE_PRODUCT_IDS

export type MobileAppProductInfo = {
  id: string
  webId?: string
  type: 'subscription' | 'package' | 'eventpass' | 'surprisepack'
  price: string
  originalPrice: string
  discount: number
  /*
   * @version 4.0
   */
  priceCurrencyCode?: string
  /*
   * @version 4.0
   */
  priceAmount?: number
  /*
   * @version 4.0
   */
  originalPriceAmount?: number
}

/**
 * Get the list of purchasable items available in the mobile app.
 */
export async function getProductListFromBridge(args?: {
  type?: ('credit' | 'member' | 'eventpass' | 'surprisepack')[]
  productIds?: string[]
  eventName?: string
}): Promise<MobileAppProductInfo[] | undefined> {
  return await Promise.race([
    new Promise<MobileAppProductInfo[]>(resolve => {
      const callbackId = createJsBridgeCallback(
        (data: { products: MobileAppProductInfo[] }) => {
          for (const product of data.products) {
            const webId = Object.entries(MOBILE_PRODUCT_IDS).find(
              it => it[1] === product.id,
            )?.[0]
            if (webId) {
              product.webId = webId
            }
          }
          resolve(data.products)
        },
      )
      callJsBridgeNativeHandler('getProducts', args, callbackId)
    }),
    sleep(1000) as Promise<undefined>,
  ])
}

/**
 * Invoke a product purchase in the mobile app.
 */
export function invokeProductPurchase(
  productIdOrItemId: string,
  options?: { couponCode?: string },
) {
  const productId = MOBILE_PRODUCT_IDS[productIdOrItemId] || productIdOrItemId

  return new Promise<boolean>(resolve => {
    const callbackId = createJsBridgeCallback((data: { success: boolean }) => {
      resolve(data.success)
    })
    callJsBridgeNativeHandler(
      'purchase',
      {
        productId,
        ...(options?.couponCode ? { couponCode: options.couponCode } : {}),
      },
      callbackId,
    )
  })
}

/**
 * Instruct the mobile app to download media from a URL.
 */
export function downloadMediaToBridge(args: {
  url?: string
  base64?: string
  imageMediaId?: string
  videoMediaId?: string
  fileName?: string
}) {
  callJsBridgeNativeHandler('downloadMedia', args)
}

export type JSBridgeMediaCallbackValue = {
  mediaId: string
  width: number
  height: number
}

/**
 * Open the camera to take a photo and upload it media server
 */
export async function takePhoto() {
  return new Promise<JSBridgeMediaCallbackValue | undefined>(resolve => {
    const callbackId = createJsBridgeCallback(
      (data: JSBridgeMediaCallbackValue) => {
        resolve(data)
      },
    )
    callJsBridgeNativeHandler('takePhoto', undefined, callbackId)
  })
}

/**
 * Open the photo gallery to select a photo and upload it to the media server
 */
export async function pickMedia(args?: { mimeTypes: string[] }) {
  return new Promise<JSBridgeMediaCallbackValue | undefined>(resolve => {
    const callbackId = createJsBridgeCallback(
      (data: JSBridgeMediaCallbackValue) => {
        resolve(data)
      },
    )
    callJsBridgeNativeHandler('mediaPicker', args, callbackId)
  })
}

export async function showToast(args: {
  content: string
  type: 'success' | 'info' | 'error'
}) {
  return new Promise<JSBridgeMediaCallbackValue | undefined>(resolve => {
    const callbackId = createJsBridgeCallback(
      (data: JSBridgeMediaCallbackValue) => {
        resolve(data)
      },
    )
    callJsBridgeNativeHandler('showToast', args, callbackId)
  })
}
